import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Building, User, Calendar, IndianRupee, Shield, TrendingUp, Edit, Trash2, LogOut, CheckCircle, Key, Lock, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

import API from '../utils/api';

const DEPARTMENTS = [
  "Founder's Office",
  "Social & Content",
  "Video & Motion",
  "Creative & Design",
  "Paid & Performance"
];

const EMPLOYMENT_TYPES = ["Intern", "Full-time"];

export default function EmployeeDetailSheet({ employee, onClose, onUpdate }) {
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [exitDate, setExitDate] = useState('');
  const [editData, setEditData] = useState({ ...employee });
  const [loading, setLoading] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const maskAadhar = (aadhar) => {
    if (!aadhar) return '-';
    return 'XXXX-XXXX-' + aadhar.slice(-4);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/employees/${employee.id}`, { withCredentials: true });
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to delete employee:', err);
    }
  };

  const handleMarkAadharVerified = async () => {
    try {
      setLoading(true);
      await axios.put(`${API}/employees/${employee.id}`, { aadhar_verified: true }, { withCredentials: true });
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to verify Aadhar:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkExit = async () => {
    if (!exitDate) return;
    try {
      setLoading(true);
      await axios.put(`${API}/employees/${employee.id}`, { 
        status: 'Inactive',
        exit_date: exitDate 
      }, { withCredentials: true });
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to mark exit:', err);
    } finally {
      setLoading(false);
      setShowExitDialog(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    const num = parseFloat(value) || 0;
    setEditData(prev => ({ ...prev, [field]: num }));
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const updatePayload = {
        name: editData.name,
        phone: editData.phone,
        email: editData.email,
        dob: editData.dob,
        department: editData.department,
        role: editData.role,
        employment_type: editData.employment_type,
        reporting_manager: editData.reporting_manager,
        base_salary: editData.base_salary,
        hra: editData.hra,
        allowances: editData.allowances,
        bonus: editData.bonus,
        deductions: editData.deductions,
        review_date: editData.review_date
      };
      await axios.put(`${API}/employees/${employee.id}`, updatePayload, { withCredentials: true });
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Failed to update employee:', err);
    } finally {
      setLoading(false);
      setShowEditDialog(false);
    }
  };

  const totalSalary = (editData.base_salary || 0) + (editData.hra || 0) + (editData.allowances || 0) + (editData.bonus || 0) - (editData.deductions || 0);

  return (
    <>
      <Sheet open={true} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-xl bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="text-white font-heading text-2xl font-bold" data-testid="employee-detail-title">
              {employee.name}
            </SheetTitle>
            <p className="font-mono text-cyan-400 text-sm" data-testid="employee-detail-id">
              {employee.employee_id}
            </p>
          </SheetHeader>

          <div className="space-y-6">
            {/* Basic Info */}
            <Section title="Basic Info" icon={<User size={16} className="text-indigo-400" />}>
              <InfoRow label="Name" value={employee.name} testId="detail-name" />
              <InfoRow label="Employee ID" value={employee.employee_id} mono testId="detail-employee-id" />
              <InfoRow label="Phone" value={employee.phone} icon={<Phone size={14} />} testId="detail-phone" />
              <InfoRow label="Email" value={employee.email} icon={<Mail size={14} />} testId="detail-email" />
              <InfoRow label="Date of Birth" value={formatDate(employee.dob)} testId="detail-dob" />
            </Section>

            {/* Job Info */}
            <Section title="Job Info" icon={<Building size={16} className="text-cyan-400" />}>
              <InfoRow label="Department" value={employee.department} testId="detail-department" />
              <InfoRow label="Role" value={employee.role} testId="detail-role" />
              <InfoRow label="Employment Type" value={employee.employment_type} testId="detail-employment-type" />
              <InfoRow label="Reporting Manager" value={employee.reporting_manager || '-'} testId="detail-manager" />
              <InfoRow label="Joining Date" value={formatDate(employee.joining_date)} testId="detail-joining-date" />
              {employee.exit_date && (
                <InfoRow label="Exit Date" value={formatDate(employee.exit_date)} className="text-rose-400" testId="detail-exit-date" />
              )}
              <InfoRow 
                label="Status" 
                value={
                  <span className={`status-badge ${employee.status.toLowerCase()}`}>
                    {employee.status}
                  </span>
                } 
                testId="detail-status"
              />
            </Section>

            {/* Salary Breakdown */}
            <Section title="Salary Breakdown" icon={<IndianRupee size={16} className="text-amber-400" />}>
              <InfoRow label="Base Salary" value={formatCurrency(employee.base_salary)} mono testId="detail-base-salary" />
              <InfoRow label="HRA" value={formatCurrency(employee.hra)} mono testId="detail-hra" />
              <InfoRow label="Allowances" value={formatCurrency(employee.allowances)} mono testId="detail-allowances" />
              <InfoRow label="Bonus" value={formatCurrency(employee.bonus)} mono testId="detail-bonus" />
              <InfoRow label="Deductions" value={`-${formatCurrency(employee.deductions)}`} mono className="text-rose-400" testId="detail-deductions" />
              <div className="border-t border-white/10 pt-3 mt-3">
                <InfoRow 
                  label="Total Salary" 
                  value={formatCurrency(employee.total_salary)} 
                  mono 
                  className="text-emerald-400 text-lg font-bold"
                  testId="detail-total-salary"
                />
              </div>
            </Section>

            {/* Compliance */}
            <Section title="Compliance" icon={<Shield size={16} className="text-emerald-400" />}>
              <InfoRow label="Aadhar" value={maskAadhar(employee.aadhar)} mono testId="detail-aadhar" />
              <div className="flex justify-between items-center">
                <span className="text-slate-500 text-sm">Aadhar Verified</span>
                <div className="flex items-center gap-2">
                  <span className={employee.aadhar_verified ? 'text-emerald-400' : 'text-amber-400'} data-testid="detail-aadhar-verified">
                    {employee.aadhar_verified ? 'Verified' : 'Pending'}
                  </span>
                  {!employee.aadhar_verified && employee.aadhar && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleMarkAadharVerified}
                      disabled={loading}
                      className="h-7 px-2 text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
                      data-testid="verify-aadhar-button"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      Verify
                    </Button>
                  )}
                </div>
              </div>
            </Section>

            {/* Growth & Reviews */}
            <Section title="Growth & Reviews" icon={<TrendingUp size={16} className="text-rose-400" />}>
              <InfoRow label="Review Date" value={formatDate(employee.review_date)} testId="detail-review-date-2" />
            </Section>

            {/* Credentials & Access */}
            <CredentialsSection employeeId={employee.employee_id} onUpdate={onUpdate} />

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
              <Button 
                onClick={() => setShowEditDialog(true)}
                className="w-full bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 border border-indigo-500/20"
                data-testid="edit-employee-button"
              >
                <Edit size={16} className="mr-2" />
                Edit Employee
              </Button>
              
              {employee.status === 'Active' && (
                <Button 
                  onClick={() => setShowExitDialog(true)}
                  className="w-full bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20"
                  data-testid="mark-exit-button"
                >
                  <LogOut size={16} className="mr-2" />
                  Mark Exit
                </Button>
              )}

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="w-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20"
                    data-testid="delete-employee-button"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#0f0f15] border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Delete Employee</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      Are you sure you want to delete {employee.name}? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDelete}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="confirm-delete-button"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">Mark Employee Exit</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-400 mb-4">
              This will mark <span className="text-white font-medium">{employee.name}</span> as inactive.
            </p>
            <label className="block text-sm text-slate-400 mb-2">Exit Date *</label>
            <Input
              type="date"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
              className="bg-black/40 border-white/10 text-white"
              data-testid="exit-date-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowExitDialog(false)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkExit}
              disabled={!exitDate || loading}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-exit-button"
            >
              {loading ? 'Processing...' : 'Confirm Exit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">Edit Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Details */}
            <FormSection title="Basic Details">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Full Name">
                  <Input
                    value={editData.name}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-name-input"
                  />
                </FormField>
                <FormField label="Phone">
                  <Input
                    value={editData.phone}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-phone-input"
                  />
                </FormField>
                <FormField label="Email">
                  <Input
                    type="email"
                    value={editData.email}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-email-input"
                  />
                </FormField>
                <FormField label="Date of Birth">
                  <Input
                    type="date"
                    value={editData.dob}
                    onChange={(e) => handleEditChange('dob', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-dob-input"
                  />
                </FormField>
              </div>
            </FormSection>

            {/* Job Details */}
            <FormSection title="Job Details">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Department">
                  <Select value={editData.department} onValueChange={(v) => handleEditChange('department', v)}>
                    <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="edit-department-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f0f15] border-white/10">
                      {DEPARTMENTS.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Role">
                  <Input
                    value={editData.role}
                    onChange={(e) => handleEditChange('role', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-role-input"
                  />
                </FormField>
                <FormField label="Employment Type">
                  <Select value={editData.employment_type} onValueChange={(v) => handleEditChange('employment_type', v)}>
                    <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="edit-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f0f15] border-white/10">
                      {EMPLOYMENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
                <FormField label="Reporting Manager">
                  <Input
                    value={editData.reporting_manager || ''}
                    onChange={(e) => handleEditChange('reporting_manager', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-manager-input"
                  />
                </FormField>
                <FormField label="Review Date">
                  <Input
                    type="date"
                    value={editData.review_date || ''}
                    onChange={(e) => handleEditChange('review_date', e.target.value)}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="edit-review-date-input"
                  />
                </FormField>
              </div>
            </FormSection>

            {/* Salary Breakdown */}
            <FormSection title="Salary Breakdown">
              <div className="grid grid-cols-3 gap-4">
                <FormField label="Base Salary">
                  <Input
                    type="number"
                    value={editData.base_salary}
                    onChange={(e) => handleNumberChange('base_salary', e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono"
                    data-testid="edit-base-salary-input"
                  />
                </FormField>
                <FormField label="HRA">
                  <Input
                    type="number"
                    value={editData.hra}
                    onChange={(e) => handleNumberChange('hra', e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono"
                    data-testid="edit-hra-input"
                  />
                </FormField>
                <FormField label="Allowances">
                  <Input
                    type="number"
                    value={editData.allowances}
                    onChange={(e) => handleNumberChange('allowances', e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono"
                    data-testid="edit-allowances-input"
                  />
                </FormField>
                <FormField label="Bonus">
                  <Input
                    type="number"
                    value={editData.bonus}
                    onChange={(e) => handleNumberChange('bonus', e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono"
                    data-testid="edit-bonus-input"
                  />
                </FormField>
                <FormField label="Deductions">
                  <Input
                    type="number"
                    value={editData.deductions}
                    onChange={(e) => handleNumberChange('deductions', e.target.value)}
                    className="bg-black/40 border-white/10 text-white font-mono"
                    data-testid="edit-deductions-input"
                  />
                </FormField>
                <FormField label="Total Salary">
                  <div className="h-10 px-3 flex items-center bg-emerald-500/10 border border-emerald-500/20 rounded-md font-mono text-emerald-400">
                    {formatCurrency(totalSalary)}
                  </div>
                </FormField>
              </div>
            </FormSection>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="save-edit-button"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400">{title}</h3>
      </div>
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value, icon, mono, className, testId }) {
  return (
    <div className="flex justify-between items-center" data-testid={testId}>
      <span className="text-slate-500 text-sm flex items-center gap-2">
        {icon}
        {label}
      </span>
      <span className={`text-slate-200 text-sm ${mono ? 'font-mono' : ''} ${className || ''}`}>
        {value}
      </span>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-sm text-slate-400 mb-2">{label}</label>
      {children}
    </div>
  );
}

const TRACKERS = ['production', 'execution', 'performance', 'clients', 'talent', 'shoots', 'deliverables', 'attendance', 'assets', 'invoices', 'approvals'];
const TASK_ONLY_TRACKERS = new Set(['production', 'execution', 'performance']);
const LEVELS = ['none', 'view', 'task_only', 'edit'];
const LEVEL_LABELS = { none: 'None', view: 'View', task_only: 'Task', edit: 'Edit' };
const LEVEL_COLORS = {
  none:      'bg-transparent border-white/5 text-slate-600 hover:text-slate-400 hover:border-white/15',
  view:      'bg-slate-500/30 border-slate-400/40 text-white',
  task_only: 'bg-amber-500/25 border-amber-400/40 text-amber-300',
  edit:      'bg-emerald-500/25 border-emerald-400/40 text-emerald-300',
};

function CredentialsSection({ employeeId, onUpdate }) {
  const [creds, setCreds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [newUsername, setNewUsername] = useState(null);
  const [overrides, setOverrides] = useState({});  // tracker → level (only explicit overrides)
  const [defaults, setDefaults] = useState({});     // tracker → level (from dept, read-only)
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/employees/${employeeId}/credentials`, { withCredentials: true });
        setCreds(data);
        setOverrides(data.permissions_override || {});
        setDefaults(data.defaults || {});
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [employeeId]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/employees/${employeeId}/credentials`, { permissions: overrides }, { withCredentials: true });
      setNewPassword(data.password);
      setNewUsername(data.username);
      setCreds({ has_credentials: true, email: data.email, username: data.username, permissions_override: data.permissions_override, defaults: data.defaults });
      setDefaults(data.defaults || {});
      toast.success('Credentials generated successfully');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to generate credentials');
    }
    finally { setCreating(false); }
  };

  const handleSavePerms = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/employees/${employeeId}/permissions`, { permissions: overrides }, { withCredentials: true });
      setOverrides(data.permissions_override || {});
      setDefaults(data.defaults || {});
      toast.success('Permissions saved successfully');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save permissions');
    }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    try {
      const { data } = await axios.post(`${API}/employees/${employeeId}/reset-password`, {}, { withCredentials: true });
      setNewPassword(data.password);
      setNewUsername(data.username);
      toast.success('Password reset successfully');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  /** What the user actually has right now: override if set, else dept default, else none */
  const effectiveLevel = (tracker) => overrides[tracker] || defaults[tracker] || 'none';

  const setLevel = (tracker, level) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (level === (defaults[tracker] || 'none')) {
        // Same as default → drop override
        delete next[tracker];
      } else {
        next[tracker] = level;
      }
      return next;
    });
  };

  const renderMatrix = () => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-slate-600 pb-1 border-b border-white/5">
        <span className="w-24">Tracker</span>
        <span className="w-16 text-center">Default</span>
        <span className="flex-1 text-right">Override</span>
      </div>
      {TRACKERS.map(tracker => {
        const def = defaults[tracker] || 'none';
        const current = effectiveLevel(tracker);
        const isOverridden = !!overrides[tracker];
        const availableLevels = TASK_ONLY_TRACKERS.has(tracker) ? LEVELS : ['none', 'view', 'edit'];
        return (
          <div key={tracker} className="flex items-center gap-2">
            <span className="w-24 text-xs text-slate-400 capitalize shrink-0">{tracker}</span>
            <span className="w-16 text-center text-[10px] text-slate-500 capitalize">{def === 'task_only' ? 'Task' : (LEVEL_LABELS[def] || 'None')}</span>
            <div className="flex-1 flex gap-1 justify-end">
              {availableLevels.map(level => (
                <button key={level} onClick={() => setLevel(tracker, level)}
                  className={`text-[10px] px-2 py-1 rounded-md border-2 transition-all font-medium min-w-[44px] ${current === level ? LEVEL_COLORS[level] : 'bg-transparent border-white/5 text-slate-600 hover:text-slate-400 hover:border-white/15'}`}
                  data-testid={`perm-${tracker}-${level}`}
                  title={current === level && isOverridden ? 'Override' : current === level ? 'Default' : ''}
                >
                  {LEVEL_LABELS[level]}
                </button>
              ))}
            </div>
            {isOverridden && <span className="text-[9px] text-indigo-400 w-12 text-right">override</span>}
            {!isOverridden && <span className="text-[9px] text-slate-700 w-12 text-right">default</span>}
          </div>
        );
      })}
    </div>
  );

  if (loading) return null;

  return (
    <Section title="Credentials & Access" icon={<Lock size={16} className="text-purple-400" />}>
      {!creds?.has_credentials ? (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">No login credentials yet. Dept defaults apply automatically — override below.</p>
          {renderMatrix()}
          <Button onClick={handleCreate} disabled={creating} className="w-full bg-purple-600 hover:bg-purple-700 h-8 text-xs" data-testid="generate-credentials-btn">
            <Key size={14} className="mr-1" /> {creating ? 'Generating...' : 'Generate Credentials'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <CopyableRow label="Username" value={creds.username} testId="cred-username" />
          <CopyableRow label="Email" value={creds.email} testId="cred-email" />
          {(newPassword || newUsername) && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3 space-y-2">
              <p className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider">Credentials (shown once — share with employee)</p>
              {newUsername && <CopyableRow label="Username" value={newUsername} testId="cred-new-username" highlight />}
              {newPassword && <CopyableRow label="Password" value={newPassword} testId="cred-new-password" highlight mono />}
            </div>
          )}
          <div className="space-y-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Access Permissions <span className="text-slate-600 normal-case">— dept default applied unless overridden</span></p>
            {renderMatrix()}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSavePerms} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="save-permissions-btn">
              {saving ? 'Saving...' : 'Save Permissions'}
            </Button>
            <Button onClick={handleResetPassword} variant="outline" className="bg-white/5 border-white/10 text-slate-300 h-8 text-xs" data-testid="reset-password-btn">
              Reset Password
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}

function CopyableRow({ label, value, testId, highlight, mono }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between gap-2" data-testid={testId}>
      <span className={`text-sm ${highlight ? 'text-amber-200' : 'text-slate-500'}`}>{label}</span>
      <div className="flex items-center gap-1.5">
        <span className={`text-sm ${highlight ? 'text-white font-bold' : 'text-slate-200'} ${mono ? 'font-mono' : ''}`}>
          {value}
        </span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-white/10 transition-colors"
          data-testid={`copy-${testId}`}
          title="Copy to clipboard"
        >
          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} className="text-slate-500 hover:text-slate-300" />}
        </button>
      </div>
    </div>
  );
}
